"use client";

import { c as _c } from "react/compiler-runtime";
import { useState } from "react";
import type { DateTime } from "effect";
import { AlertTriangle, ChevronDown, ChevronUp, ExternalLink } from "lucide-react";
import { StatusBanner, StatusBannerRow, StatusBannerContent, StatusBannerActions, StatusBannerIconButton, STATUS_BANNER_INSET, STATUS_BANNER_PRESS, statusBannerColorMap } from "@/components/ui/status-banner";
import { useOrgInvoices } from "@/entities/billing/model";
import type { InvoiceResponse } from "@/contexts/billing/collections/presentation/rpc/org-admin-billing-rpc";
import { formatBillingCurrency, formatBillingDate } from "./billing-helpers";
interface OutstandingInvoicesBannerProps {
  className?: string;
  /** Whether the viewer is the org's billing admin (gate `isAdmin`). */
  isAdmin: boolean;
  /** Count of OPEN (finalised + unpaid) Stripe invoices — the gate snapshot. */
  outstandingInvoiceCount: number;
  /** Total owed in cents; null when there is no balance. */
  outstandingTotalCents: number | null;
  /** Currency of the balance; null when there is no balance. */
  outstandingCurrency: string | null;
  /** Oldest unpaid due date across the outstanding invoices. */
  oldestUnpaidDueAt: DateTime.Utc | null;
  /** `!account.bankTransferOnly` — drives the "pay by card for instant clearing" nudge. */
  payByCardAvailable: boolean;
}

/**
 * Persistent amber banner shown to org-admins when the org has outstanding
 * (open / unpaid) Stripe invoices but is NOT in a lockout phase. Collapsible to
 * a thin stripe but not fully dismissible (matches SoftLockBanner). Caps at
 * amber styling — red is reserved for the soft/hard-lock banners that take
 * visual + functional priority.
 *
 * The summary (count / total / oldest-due) rides on the already-fetched billing
 * gate status (passed as props — no extra fetch). The expanded per-invoice list
 * lazily fetches via useOrgInvoices only when expanded.
 *
 * Spec: docs/specs/billing/2026-06-10-001 §5.4 / §5.7.
 */
export function OutstandingInvoicesBanner(t0) {
  const $ = _c(14);
  const {
    className,
    isAdmin,
    outstandingInvoiceCount,
    outstandingTotalCents,
    outstandingCurrency,
    oldestUnpaidDueAt,
    payByCardAvailable
  } = t0;
  const [isCollapsed, setIsCollapsed] = useState(false);
  if (!isAdmin || outstandingInvoiceCount <= 0) {
    return null;
  }
  const currency = outstandingCurrency ?? "aud";
  const totalCents = outstandingTotalCents ?? 0;
  let t1;
  if ($[0] !== currency || $[1] !== totalCents) {
    t1 = formatBillingCurrency(totalCents, currency);
    $[0] = currency;
    $[1] = totalCents;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== oldestUnpaidDueAt) {
    t2 = oldestUnpaidDueAt ? ` · oldest due ${formatBillingDate(oldestUnpaidDueAt)}` : "";
    $[3] = oldestUnpaidDueAt;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const summary = `${outstandingInvoiceCount} unpaid ${outstandingInvoiceCount === 1 ? "invoice" : "invoices"} · ${t1}${t2}`;
  let t3;
  if ($[5] !== className || $[6] !== currency || $[7] !== isCollapsed || $[8] !== oldestUnpaidDueAt || $[9] !== outstandingInvoiceCount || $[10] !== payByCardAvailable || $[11] !== summary || $[12] !== totalCents) {
    t3 = isCollapsed ? <CollapsedBanner summary={summary} onClick={() => setIsCollapsed(false)} className={className} /> : <ExpandedBanner count={outstandingInvoiceCount} totalCents={totalCents} currency={currency} oldestUnpaidDueAt={oldestUnpaidDueAt} payByCardAvailable={payByCardAvailable} onCollapse={() => setIsCollapsed(true)} className={className} />;
    $[5] = className;
    $[6] = currency;
    $[7] = isCollapsed;
    $[8] = oldestUnpaidDueAt;
    $[9] = outstandingInvoiceCount;
    $[10] = payByCardAvailable;
    $[11] = summary;
    $[12] = totalCents;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
}
function CollapsedBanner(t0) {
  const $ = _c(10);
  const {
    summary,
    onClick,
    className
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <AlertTriangle className="h-3.5 w-3.5 shrink-0" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== summary) {
    t2 = <span>{summary}</span>;
    $[1] = summary;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <ChevronDown className="ml-1 h-3.5 w-3.5" />;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== onClick || $[5] !== t2) {
    t4 = <button type="button" onClick={onClick} className={`flex w-full items-center justify-center gap-2 px-4 py-2 text-sm font-medium text-white transition-colors cursor-pointer ${STATUS_BANNER_PRESS}`}>{t1}{t2}{t3}</button>;
    $[4] = onClick;
    $[5] = t2;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== className || $[8] !== t4) {
    t5 = <StatusBanner color="amber" className={className}>{t4}</StatusBanner>;
    $[7] = className;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
}
function ExpandedBanner(t0) {
  const $ = _c(55);
  const {
    count,
    totalCents,
    currency,
    oldestUnpaidDueAt,
    payByCardAvailable,
    onCollapse,
    className
  } = t0;
  const {
    data: invoices
  } = useOrgInvoices(25, true);
  let T0;
  let T1;
  let T2;
  let t1;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== className || $[1] !== count || $[2] !== currency || $[3] !== invoices || $[4] !== oldestUnpaidDueAt || $[5] !== totalCents) {
    const openInvoices = (invoices ?? []).filter(_temp);
    T2 = StatusBanner;
    t8 = "amber";
    t9 = className;
    T1 = StatusBannerRow;
    t7 = "items-start";
    T0 = StatusBannerContent;
    t5 = "flex items-start gap-3";
    if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
      t6 = <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-white/80" />;
      $[18] = t6;
    } else {
      t6 = $[18];
    }
    t1 = "flex-1";
    const t10 = count === 1 ? "invoice" : "invoices";
    let t11;
    if ($[19] !== currency || $[20] !== totalCents) {
      t11 = formatBillingCurrency(totalCents, currency);
      $[19] = currency;
      $[20] = totalCents;
      $[21] = t11;
    } else {
      t11 = $[21];
    }
    if ($[22] !== count || $[23] !== t10 || $[24] !== t11) {
      t2 = <p className="text-sm font-medium text-white">You have {count} unpaid {t10}{" "}totalling {t11}</p>;
      $[22] = count;
      $[23] = t10;
      $[24] = t11;
      $[25] = t2;
    } else {
      t2 = $[25];
    }
    if ($[26] !== oldestUnpaidDueAt) {
      t3 = oldestUnpaidDueAt && <p className="mt-0.5 text-xs">Oldest due {formatBillingDate(oldestUnpaidDueAt)}</p>;
      $[26] = oldestUnpaidDueAt;
      $[27] = t3;
    } else {
      t3 = $[27];
    }
    t4 = openInvoices.length > 0 && <ul className="mt-2 space-y-1.5">{openInvoices.map(_temp2)}</ul>;
    $[0] = className;
    $[1] = count;
    $[2] = currency;
    $[3] = invoices;
    $[4] = oldestUnpaidDueAt;
    $[5] = totalCents;
    $[6] = T0;
    $[7] = T1;
    $[8] = T2;
    $[9] = t1;
    $[10] = t2;
    $[11] = t3;
    $[12] = t4;
    $[13] = t5;
    $[14] = t6;
    $[15] = t7;
    $[16] = t8;
    $[17] = t9;
  } else {
    T0 = $[6];
    T1 = $[7];
    T2 = $[8];
    t1 = $[9];
    t2 = $[10];
    t3 = $[11];
    t4 = $[12];
    t5 = $[13];
    t6 = $[14];
    t7 = $[15];
    t8 = $[16];
    t9 = $[17];
  }
  let t10;
  if ($[28] !== payByCardAvailable) {
    t10 = <ClearingLagNote payByCardAvailable={payByCardAvailable} />;
    $[28] = payByCardAvailable;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <a href="/settings/billing" className="mt-2 inline-flex items-center text-xs font-medium underline underline-offset-2">View all invoices</a>;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t1 || $[32] !== t10 || $[33] !== t2 || $[34] !== t3 || $[35] !== t4) {
    t12 = <div className={t1}>{t2}{t3}{t4}{t10}{t11}</div>;
    $[31] = t1;
    $[32] = t10;
    $[33] = t2;
    $[34] = t3;
    $[35] = t4;
    $[36] = t12;
  } else {
    t12 = $[36];
  }
  let t13;
  if ($[37] !== T0 || $[38] !== t12 || $[39] !== t5 || $[40] !== t6) {
    t13 = <T0 className={t5}>{t6}{t12}</T0>;
    $[37] = T0;
    $[38] = t12;
    $[39] = t5;
    $[40] = t6;
    $[41] = t13;
  } else {
    t13 = $[41];
  }
  let t14;
  if ($[42] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ChevronUp className="h-4 w-4" />;
    $[42] = t14;
  } else {
    t14 = $[42];
  }
  let t15;
  if ($[43] !== onCollapse) {
    t15 = <StatusBannerActions><StatusBannerIconButton color="amber" onClick={onCollapse} aria-label="Collapse banner">{t14}</StatusBannerIconButton></StatusBannerActions>;
    $[43] = onCollapse;
    $[44] = t15;
  } else {
    t15 = $[44];
  }
  let t16;
  if ($[45] !== T1 || $[46] !== t13 || $[47] !== t15 || $[48] !== t7) {
    t16 = <T1 className={t7}>{t13}{t15}</T1>;
    $[45] = T1;
    $[46] = t13;
    $[47] = t15;
    $[48] = t7;
    $[49] = t16;
  } else {
    t16 = $[49];
  }
  let t17;
  if ($[50] !== T2 || $[51] !== t16 || $[52] !== t8 || $[53] !== t9) {
    t17 = <T2 color={t8} className={t9}>{t16}</T2>;
    $[50] = T2;
    $[51] = t16;
    $[52] = t8;
    $[53] = t9;
    $[54] = t17;
  } else {
    t17 = $[54];
  }
  return t17;
}
function _temp2(inv_0) {
  return <InvoiceRow key={inv_0.id} invoice={inv_0} />;
}
function _temp(inv) {
  return inv.status === "open";
}
function InvoiceRow(t0) {
  const $ = _c(20);
  const {
    invoice
  } = t0;
  const t1 = invoice.number ?? "Invoice";
  let t2;
  if ($[0] !== t1) {
    t2 = <span className="font-medium">{t1}</span>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== invoice.amountDue || $[3] !== invoice.currency) {
    t3 = formatBillingCurrency(invoice.amountDue, invoice.currency);
    $[2] = invoice.amountDue;
    $[3] = invoice.currency;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <span className="ml-2">{t3}</span>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== invoice.date) {
    t5 = formatBillingDate(invoice.date);
    $[7] = invoice.date;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== t5) {
    t6 = <span className="ml-2">{t5}</span>;
    $[9] = t5;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== t2 || $[12] !== t4 || $[13] !== t6) {
    t7 = <span className="min-w-0 truncate text-xs text-white">{t2}{t4}{t6}</span>;
    $[11] = t2;
    $[12] = t4;
    $[13] = t6;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== invoice.hostedInvoiceUrl) {
    t8 = invoice.hostedInvoiceUrl && <a href={invoice.hostedInvoiceUrl} target="_blank" rel="noopener noreferrer" className={`inline-flex shrink-0 items-center gap-1 rounded-md border px-2.5 py-1 text-xs font-medium transition-colors active:scale-[0.97] ${statusBannerColorMap.amber.actionButton}`}>Pay<ExternalLink className="h-3 w-3" /></a>;
    $[15] = invoice.hostedInvoiceUrl;
    $[16] = t8;
  } else {
    t8 = $[16];
  }
  let t9;
  if ($[17] !== t7 || $[18] !== t8) {
    t9 = <li className={`flex items-center justify-between gap-3 rounded-md px-3 py-1.5 ${STATUS_BANNER_INSET}`}>{t7}{t8}</li>;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  return t9;
}
function ClearingLagNote(t0) {
  const $ = _c(2);
  const {
    payByCardAvailable
  } = t0;
  const t1 = payByCardAvailable ? " Want it cleared instantly? Pay by card on the invoice page." : "";
  let t2;
  if ($[0] !== t1) {
    t2 = <p className="mt-2 text-xs leading-relaxed">Paying by bank transfer can take 2–3 business days to clear and update here.{t1}</p>;
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  return t2;
}